import { useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Stepper,
    Step,
    StepLabel,
    Typography,
    Button,
    Paper,
    Backdrop,
    CircularProgress,
} from "@mui/material";

import { useSelector } from "react-redux";
import GeneralDetails from "./GeneralDetails";
import RuleAssociations from "./RuleAssociation";
import ReviewAndCreate from "./ReviewAndCreate";
import OutcomeGenerator, { newOutcomeBlock } from "./OutcomeGenerator";
import { createRule, getAttributesMetaData, getRuleById, updateRule } from "../../../controllers/RuleController";
import moment from "moment";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Message from "../../../components/Message";
import { getCatalogLocations, getCatalogs, getCategoriesByCatalog, getCustomerGroups, getProductsByCatalog, getRequestSources, getSkusByCatalog } from "../../../controllers/CatalogController";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconButton } from "@mui/material";
import { normalizeForm, reformatConditionsForEdit, denormalizeFrequency, getRuleStatus } from "./RuleUtils";
import { getCooloffMinutesFromConfigs, hasSelectedBaseCondition, validateRule } from "./RuleValidation";


const steps = ["General Details", "Rule Associations", "Outcome Generator", "Review & Save"];

export default function CreatePricingRule() {
    const { id } = useParams();
    const [activeStep, setActiveStep] = useState(0);
    const RULE_ENUMS = useSelector((state) => state.globalData.enums);
    const RULE_CONFIGS = useSelector((state) => state.globalData.configs);
    const timezone = useSelector((state) => state.globalData.timezone);
    const [apiMessage, setApiMessage] = useState({
        open: false,
        type: "success",
        message: "",
    });
    const navigate = useNavigate();
    const location = useLocation();
    const [isEditable, setIsEditable] = useState(true);
    const [loading, setLoading] = useState(false);
    const [catalogs, setCatalogs] = useState([]);
    const [locations, setLocations] = useState({});
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [skus, setSkus] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState("");
    const [selectedProduct, setSelectedProduct] = useState("");
    const [selectedSku, setSelectedSku] = useState("");
    const [selectedStores, setSelectedStores] = useState([]);
    const [selectedGeoTags, setSelectedGeoTags] = useState([]);
    const [attributeConfig, setAttributeConfig] = useState({ attributesData: null });
    const initialFormState = {
        "ruleType": "",
        "description": "",
        "startDate": "",
        "endDate": "",
        "frequency": null,
        "masterDisable": false,
        "storeIds": [],
        "geoTagIds": [],
        "catalogId": "",
        "priority": 1,
        "stopOnMatch": false,
        "ruleState": "EDITING",
        "when": [],
        // Pre-seed one empty outcome block so a brand new rule lands on
        // the Outcome Generator step with a block already visible, instead
        // of an empty state the user has to click "Add Outcome" to fill.
        "then": { subConditions: [newOutcomeBlock()] },
    };

    const [form, setForm] = useState(initialFormState);
    const [allowNavigation, setAllowNavigation] = useState(false);
    const initialFormRef = useRef(JSON.stringify(initialFormState));
    const isHydratingEditRef = useRef(false);

    const cooloffMinutes = useMemo(
        () => getCooloffMinutesFromConfigs(RULE_CONFIGS),
        [RULE_CONFIGS]
    );
    const normalizedForm = useMemo(() => normalizeForm(form), [form]);

    const hasUnsavedChanges = useMemo(() => {
        if (!initialFormRef.current) return false;

        return (
            JSON.stringify(normalizedForm) !== initialFormRef.current
        );
    }, [normalizedForm]);


    const shouldBlockNavigation = isEditable && hasUnsavedChanges && !allowNavigation;

    useEffect(() => {
        if (!shouldBlockNavigation) return;

        const message = "You have unsaved changes. Do you want to leave this page?";

        const handleBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = "";
        };

        // Add a history entry so browser back stays on this page until user confirms leaving.
        window.history.pushState(null, "", window.location.href);

        const handlePopState = () => {
            const shouldLeave = window.confirm(message);
            if (shouldLeave) {
                setAllowNavigation(true);
                window.removeEventListener("popstate", handlePopState);
                navigate("/", {
                    state: { selectedCatalogId: location.state?.["selectedCatalogId"] || "", }
                });
                return;
            }

            window.history.pushState(null, "", window.location.href);
        };

        const handleDocumentClick = (event) => {
            const anchor = event.target?.closest?.("a[href]");
            if (!anchor) return;

            const href = anchor.getAttribute("href");
            if (!href || href.startsWith("#") || href.startsWith("javascript:")) return;

            const nextUrl = new URL(anchor.href, window.location.origin);
            const currentUrl = new URL(window.location.href);
            const isSameDestination =
                nextUrl.pathname === currentUrl.pathname &&
                nextUrl.search === currentUrl.search &&
                nextUrl.hash === currentUrl.hash;

            if (isSameDestination) return;

            const shouldLeave = window.confirm(message);
            if (!shouldLeave) {
                event.preventDefault();
                event.stopPropagation();
                return;
            }

            setAllowNavigation(true);
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        window.addEventListener("popstate", handlePopState);
        document.addEventListener("click", handleDocumentClick, true);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
            window.removeEventListener("popstate", handlePopState);
            document.removeEventListener("click", handleDocumentClick, true);
        };
    }, [navigate, shouldBlockNavigation]);

    useEffect(() => {
        getAttributesMeta()
        getCatalogList();
        fetchCustomerGroups();
        fetchRequestSources();
    }, []);

    const getAttributesMeta = async () => {
        try {
            setLoading(true);
            const res = await getAttributesMetaData();
            setAttributeConfig(res);
        } catch (err) {
            console.log(err);
            setAttributeConfig([]);
        } finally {
            setLoading(false);
        }
    }

    const getCatalogList = async () => {
        try {
            setLoading(true);
            const res = await getCatalogs();
            if (res && res.length > 0) {
                setCatalogs(res);
            }
            setLoading(false);
        } catch (err) {
            setLoading(false);
            console.log(err);
        }
    };

    useEffect(() => {
        if (form.catalogId) {
            if (isHydratingEditRef.current) {
                isHydratingEditRef.current = false;
                return;
            }
            getCatalogLocationAndStore(form.catalogId);
            getCategoriesOfCatalog(form.catalogId);
            getCatalogProducts(form.catalogId);
            getCatalogSkus(form.catalogId);
        }
    }, [form.catalogId]);

    const fetchCustomerGroups = async () => {
        try {
            setLoading(true);
            const res = await getCustomerGroups();
            setLocations((prev) => ({ ...prev, customerGroups: res }));
        } catch (err) {
            console.log(err);
        }
        finally {
            setLoading(false);
        }
    }

    const fetchRequestSources = async () => {
        try {
            setLoading(true);
            const res = await getRequestSources();
            setLocations((prev) => ({ ...prev, requestSources: res }));
        } catch (err) {
            console.log(err);
        }
        finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (form.catalogId) {
            const categoryIds = selectedCategory ? (Array.isArray(selectedCategory)
                ? selectedCategory.map((c) => c.id)
                : selectedCategory.id) : ""
            const productIds = selectedProduct ? (Array.isArray(selectedProduct)
                ? selectedProduct.map((c) => c.id)
                : selectedProduct.id) : ""

            getCatalogProducts(form.catalogId, categoryIds);
            getCatalogSkus(form.catalogId, categoryIds, productIds);
        }
    }, [selectedCategory]);


    useEffect(() => {
        if (form.catalogId) {
            const categoryIds = selectedCategory ? (Array.isArray(selectedCategory)
                ? selectedCategory.map((c) => c.id)
                : selectedCategory.id) : ""
            const productIds = selectedProduct ? (Array.isArray(selectedProduct)
                ? selectedProduct.map((c) => c.id)
                : selectedProduct.id) : ""



            getCatalogSkus(form.catalogId, categoryIds, productIds);
        }
    }, [selectedProduct]);

    const getCatalogProducts = async (catalogId, categoryId = "") => {
        try {
            setLoading(true);
            const res = await getProductsByCatalog({ catalogId, categoryId });
            setProducts(res);
        } catch (err) {
            console.log(err);
        }
        finally {
            setLoading(false);
        }
    }

    const getCatalogSkus = async (catalogId, categoryId = "", productId = "") => {
        try {
            setLoading(true);
            const res = await getSkusByCatalog({ catalogId, categoryId, productId });
            setLoading(false);
            setSkus(res);
        } catch (err) {
            setLoading(false);
            console.log(err);
        }
    }

    const getCategoriesOfCatalog = async (catalogId) => {
        try {
            setLoading(true);
            const res = await getCategoriesByCatalog(catalogId);
            setCategories(res);
        } catch (err) {
            console.log(err);
        }
        finally {
            setLoading(false);
        }
    }

    const getCatalogLocationAndStore = async (catalogId) => {
        try {
            setLoading(true);
            const res = await getCatalogLocations(catalogId);
            setLocations((prev) => ({ ...prev, stores: res.stores, geoTags: res.geo_tags }));
        } catch (err) {
            console.log(err)
        }
        finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchRule(id);
        }
    }, [id]);

    const fetchRule = async (ruleId) => {
        try {
            setLoading(true);
            const res = await getRuleById(ruleId);
            if (res) {
                const data = {
                    ...res,

                    startDate: res.startDate
                        ? moment.utc(res.startDate).local().format("YYYY-MM-DD")
                        : "",

                    endDate: res.endDate
                        ? moment.utc(res.endDate).local().format("YYYY-MM-DD")
                        : "",
                    originalEndDate: res.endDate
                        ? moment.utc(res.endDate).local().format("YYYY-MM-DD")
                        : "",
                    frequency: denormalizeFrequency(res.frequency, timezone),
                    when: reformatConditionsForEdit(res.when || []),
                    then: prepareThenForEdit({
                        ...res.then,
                        subConditions: (res.then?.subConditions || []).map((block) => ({
                            ...block,
                            when: (block.when || [])
                        }))
                    })
                };

                setForm(data);
                initialFormRef.current = JSON.stringify(res);
                await hydrateSelectionsFromRule(data);
                const isCompleted = getRuleStatus(res) === "COMPLETED";

                if ((data.ruleState !== 'EDITING' && data.ruleState !== 'PUBLISHED') || data.ruleType == "LIQUIDATION_SALE" || isCompleted) {
                    setIsEditable(false);
                    setActiveStep(3);
                }
            }
        } catch (err) {
            setApiMessage({
                open: true,
                type: "error",
                message: err?.message || "Something went wrong",
            });
        }
        finally {
            setLoading(false);
        }
    };

    const hydrateSelectionsFromRule = async (res) => {
        const catalogId = res.catalogId;
        if (!catalogId) return;
        // extract raw saved values from when conditions
        const categoryCond = res.when.find(
            (c) => c.attributeType === "CATEGORY" && c.attributeFilterValue
        );
        const productCond = res.when.find(
            (c) => c.attributeType === "PRODUCT" && c.attributeFilterValue
        );
        const skuCond = res.when.find(
            (c) => c.attributeType === "SKU" && c.attributeFilterValue
        );

        const rawCategory = categoryCond?.attributeFilterValue ?? null;
        const rawProduct = productCond?.attributeFilterValue ?? null;
        const rawSku = skuCond?.attributeFilterValue ?? null;

        const toIdParam = (val) => {
            if (!val) return "";
            if (Array.isArray(val)) {
                return val.map((v) =>
                    typeof v === "object" ? v?.id ?? v?.categoryId ?? v?.productId ?? v?.skuId ?? "" : v
                );
            }
            if (typeof val === "object") {
                return val?.id ?? val?.categoryId ?? val?.productId ?? val?.skuId ?? "";
            }
            return val;
        };

        const categoryId = toIdParam(rawCategory);
        const productId = toIdParam(rawProduct);

        try {
            setLoading(true);

            // always load all categories for catalog
            const [allCategories, allProducts, allSkus] = await Promise.all([
                getCategoriesByCatalog(catalogId),
                getProductsByCatalog({ catalogId, categoryId }),
                getSkusByCatalog({ catalogId, categoryId, productId }),
            ]);

            setCategories(allCategories || []);
            setProducts(allProducts || []);
            setSkus(allSkus || []);

            // resolve selectedCategory object from loaded categories
            if (rawCategory) {
                const rawId = toIdParam(rawCategory);
                const matchedCategory = (allCategories || []).find(
                    (c) => String(c.id ?? c.categoryId) === String(Array.isArray(rawId) ? rawId[0] : rawId)
                );
                setSelectedCategory(matchedCategory ?? rawCategory);
            }

            // resolve selectedProduct object from loaded products
            if (rawProduct) {
                const rawId = toIdParam(rawProduct);
                const matchedProduct = (allProducts || []).find(
                    (p) => String(p.id ?? p.productId) === String(Array.isArray(rawId) ? rawId[0] : rawId)
                );
                setSelectedProduct(matchedProduct ?? rawProduct);
            }

            // resolve selectedSku object from loaded skus
            if (rawSku) {
                const rawId = toIdParam(rawSku);
                const matchedSku = (allSkus || []).find(
                    (s) => String(s.productItemId ?? s.id ?? s.skuId) === String(Array.isArray(rawId) ? rawId[0] : rawId)
                );
                setSelectedSku(matchedSku ?? rawSku);
            }

        } catch (err) {
            console.log(err);
        }
        finally {
            setLoading(false);
        }
    };

    const errors = useMemo(() => validateRule(form, attributeConfig, cooloffMinutes), [form, cooloffMinutes, attributeConfig]);

    const handleNext = () => {
        setActiveStep((s) => Math.min(s + 1, steps.length - 1));
    };

    const handlePrev = () => setActiveStep((s) => Math.max(s - 1, 0));

    const setField = (key, value) => {
        if (key === "catalogId") {
            setForm((prev) => {
                if (prev.catalogId === value) {
                    return prev;
                }

                return {
                    ...prev,
                    catalogId: value,
                    storeIds: [],
                    geoTagIds: [],
                    when: [],
                    then: { subConditions: [newOutcomeBlock()] },
                };
            });

            setSelectedCategory("");
            setSelectedProduct("");
            setSelectedSku("");
            setSelectedStores([]);
            setSelectedGeoTags([]);
            return;
        }

        setForm((p) => ({ ...p, [key]: value }));
    };

    const prepareThenForEdit = (thenObj) => {
        if (!thenObj) return thenObj;

        let { discount, subConditions = [] } = thenObj;

        // Reformat IN clause values in nested outcome conditions
        subConditions = (subConditions || []).map((sc) => ({
            ...sc,
            when: reformatConditionsForEdit(sc.when || []),
        }));

        if (discount) {
            let found = false;

            subConditions = subConditions.map((sc) => {
                if (!found && (!sc.when || sc.when.length === 0)) {
                    found = true;
                    return {
                        ...sc,
                        then: {
                            ...sc.then,
                            discount: discount
                        }
                    };
                }
                return sc;
            });

            if (!found) {
                subConditions.push({
                    when: [],
                    then: {
                        discount: discount,
                        subConditions: null
                    }
                });
            }

            discount = null;
        }

        return {
            ...thenObj,
            discount,
            subConditions
        };
    };

    const handleSaveRule = async () => {
        let data = normalizeForm(form, timezone);

        if (id) {
            handleUpdateRule(id, data);
        } else {
            handleCreateRule(JSON.stringify(data));
        }
    };

    const handleUpdateRule = async (id, data) => {
        try {
            setLoading(true);
            await updateRule(id, data);
            setLoading(false);
            setAllowNavigation(true);
            setApiMessage({
                open: true,
                type: "success",
                message: "Successfully updated Rule"
            });
            navigate("/", {
                state: { selectedCatalogId: location.state?.["selectedCatalogId"] || "", }
            });
        } catch (err) {
            if (err) {
                console.log(err);
                setLoading(false);
                setApiMessage({
                    open: true,
                    type: "error",
                    message: err?.message || "Something went wrong",
                });
            }
        }
    }

    const handleCreateRule = async (data) => {
        try {
            setLoading(true);
            await createRule(data);
            setLoading(false);
            setAllowNavigation(true);
            setApiMessage({
                open: true,
                type: "success",
                message: "Successfully created Rule"
            });
            navigate("/", {
                state: { selectedCatalogId: location.state?.["selectedCatalogId"] || "", }
            });
        } catch (err) {
            if (err) {
                console.log(err);
                setLoading(false);
                setApiMessage({
                    open: true,
                    type: "error",
                    message: err?.message || "Something went wrong",
                });
            }
        }
    }

    const handleStepClick = (stepIndex) => {
        if (stepIndex <= activeStep) {
            setActiveStep(stepIndex);
        }
    };
    
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                minHeight: "100vh",
                backgroundColor: "#f8fafc",
            }}
        >
            <Backdrop
                open={loading}
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 999,
                    color: "#fff",
                    backdropFilter: "blur(3px)",
                }}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
            <Box
                sx={{
                    px: 3,
                    py: 2,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "#fff",
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    display: "flex",
                    alignItems: "center",
                    gap: 2
                }}
            >
                <IconButton
                    onClick={() => {
                        if (shouldBlockNavigation) {
                            const confirmLeave = window.confirm(
                                "You have unsaved changes. Are you sure you want to leave?"
                            );
                            if (!confirmLeave) return;
                        }
                        navigate("/", {
                            state: { selectedCatalogId: location.state?.["selectedCatalogId"] || "" },
                        });
                    }}
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundColor: "error.main",
                        color: "#fff",
                        "&:hover": {
                            backgroundColor: "error.dark",
                        },
                    }}
                >
                    <ArrowBackIcon />
                </IconButton>

                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    Pricing Rule Builder
                </Typography>
            </Box>

            {/* Stepper */}
            <Box
                sx={{
                    flex: 1,
                    width: "100%",
                    maxWidth: 1600,
                    mx: "auto",
                    p: 3,
                    display: "flex",
                    gap: 4,
                    flexDirection: { xs: "column", md: "row" }, // 👈 responsive
                    alignItems: "flex-start"
                }}
            >

                {isEditable && (
                    <Box
                        sx={{
                            width: { xs: "100%", md: 260 },
                            flexShrink: 0,
                            position: { md: "sticky" },
                            top: 100,
                            height: "fit-content"
                        }}
                    >
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                border: "1px solid",
                                borderColor: "divider"
                            }}
                        >
                            <Stepper orientation="vertical" activeStep={activeStep}>
                                {steps.map((label, index) => (
                                    <Step key={label}>
                                        <StepLabel
                                            onClick={() => handleStepClick(index)}
                                            sx={{
                                                cursor: index <= activeStep ? "pointer" : "not-allowed",
                                                "& .MuiStepLabel-label": {
                                                    fontWeight: index === activeStep ? 700 : 500
                                                }
                                            }}
                                        >
                                            {label}
                                        </StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                        </Paper>
                    </Box>)}
                {/* </Box> */}

                {/* Step Content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            borderRadius: 3,
                            border: "1px solid",
                            borderColor: "divider",
                            minHeight: 650
                        }}
                    >

                        {activeStep === 0 && (
                            <GeneralDetails
                                form={form}
                                setField={setField}
                                errors={errors}
                                enums={RULE_ENUMS}
                                configs={RULE_CONFIGS}
                            />
                        )}

                        {activeStep === 1 && (
                            <RuleAssociations
                                form={form}
                                setField={setField}
                                errors={errors}
                                enums={RULE_ENUMS}
                                catalogs={catalogs}
                                locations={locations}
                                categories={categories}
                                products={products}
                                skus={skus}
                                setSelectedCategory={setSelectedCategory}
                                setSelectedProduct={setSelectedProduct}
                                setSelectedSku={setSelectedSku}
                                setSelectedStores={setSelectedStores}
                                setSelectedGeoTags={setSelectedGeoTags}
                                attributeConfig={attributeConfig}
                            />
                        )}

                        {activeStep === 2 && (
                            <OutcomeGenerator
                                form={form}
                                setField={setField}
                                errors={errors}
                                enums={RULE_ENUMS}
                                catalogs={catalogs}
                                locations={locations}
                                categories={categories}
                                products={products}
                                skus={skus}
                                selectedSku={selectedSku}
                                selectedProduct={selectedProduct}
                                selectedCategory={selectedCategory}
                                attributeConfig={attributeConfig}

                            />
                        )}

                        {activeStep === 3 && (
                            <ReviewAndCreate
                                errors={errors}
                                onBack={() => setActiveStep(2)}
                                onEditGeneral={() => setActiveStep(0)}
                                onEditAssociation={() => setActiveStep(1)}
                                onEditOutcome={() => setActiveStep(2)}
                                form={form}
                                isEditable={isEditable}
                                catalogs={catalogs}
                                locations={locations}
                                categories={categories}
                                products={products}
                                skus={skus}
                                attributeConfig={attributeConfig}
                                configs={RULE_CONFIGS}
                            />
                        )}
                    </Paper>
                </Box>
            </Box>

            {/* Footer Buttons */}
            {
                isEditable && <Box
                    sx={{
                        position: "sticky",
                        bottom: 0,
                        backgroundColor: "#fff",
                        borderTop: "1px solid",
                        borderColor: "divider",
                        py: 2,
                        zIndex: 10,
                    }}
                >
                    <Box
                        sx={{
                            maxWidth: 1400,
                            mx: "auto",
                            px: 3,
                            display: "flex",
                            justifyContent: "space-between"
                        }}
                    >

                        <Button
                            variant="outlined"
                            disabled={activeStep === 0}
                            onClick={handlePrev}
                        >
                            Prev
                        </Button>

                        {activeStep < steps.length - 1 ? (
                            <Button
                                variant="contained"
                                onClick={handleNext}
                                disabled={
                                    (activeStep === 1 &&
                                        (!(form.catalogId && hasSelectedBaseCondition(form.when)))) ||
                                    (activeStep === 2 && form.then?.subConditions?.length < 1)
                                }
                            >
                                Next
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                onClick={handleSaveRule}
                                disabled={Object.keys(errors).length > 0}
                            >
                                {id ? "Update Rule" : "Create Rule"}
                            </Button>
                        )}
                    </Box>
                </Box>
            }
            <Message
                open={apiMessage.open}
                type={apiMessage.type}
                message={apiMessage.message}
                onClose={() => setApiMessage((p) => ({ ...p, open: false }))}
            />
        </Box >
    );
}
